/* eslint-disable import/no-named-as-default */
"use client";

import { currencyFormatter, Vehicle, VehicleStatusType } from "@alba-cars/common-modules";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";

import useViewerCount from "../_hooks/useViewersCount";
import WarrantyDownpaymentCard from "./inspection-report/WarrantyDownpaymentCard";
import TestDriveButton from "./TestDriveButton";
import PhoneNumberModal from "@/components/common/modals/PhoneNumberModal";
import IconEye from "@/components/Icons/IconEye";
import Button from "@/components/ui/buttons/Button";
import { CarFavoriteButton } from "@/components/ui/buttons/CarFavoriteButton";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getCarMonthlyInstallment, getCarPrice, slugify } from "@/lib/utils";
import { phoneNumber } from "@/utils/constants";
import { getCarNameTitle } from "@/utils/utils";

const CarSidebar = ({ car }: { car: Vehicle }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isPhoneNumberModalOpen, setIsPhoneNumberModalOpen] = useState(false);

  const trim = car?.trim ?? "";
  const refId = car?.refId ?? "";
  const make = car?.make?.name ?? "";
  const model = car?.model?.name ?? "";

  const { data: viewerCount = 10 } = useViewerCount(car.id);

  const carPrice = getCarPrice(car);
  const installment = getCarMonthlyInstallment(car);

  const isTabletOrLarger = useMediaQuery("(min-width: 768px)");


    const isSold = car.status === VehicleStatusType.SOLD;
    const buttonDisabled = isSold;
  
    const buttonText = isSold ? "Vehicle Sold" : "Book a free test drive";
  return (
    <div className="flex flex-col gap-y-4">
      <div className="rounded-3xl bg-white px-7 pb-4 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="max-w-xs text-wrap font-urbanist text-3xl font-semibold text-black">
            {getCarNameTitle(car, { showTrim: true })}
          </h1>

          <CarFavoriteButton carId={car.id} className="transition-transform duration-700 hover:scale-125" withTooltip />
        </div>
        <div className="flex items-center">
          <p className="font-inter text-base font-medium text-black text-opacity-50">Stock no: {car.chassisNumber}</p>
        </div>

        <div className="mt-6 rounded-2xl">
          <p className="mb-2 flex items-end font-urbanist text-3xl font-bold">
            {!!carPrice && currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(carPrice)}
            <span className="mb-1 ml-2 font-inter text-xs text-black text-opacity-50">
              {car?.finance?.vatInclusive === "vat_exclusive" ? "(Exclusive of VAT)" : "(Inclusive of VAT)"}
            </span>
          </p>
          <span className="inter text-base font-bold text-success">
            {!!installment && currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(installment)}
            {""}/Month
          </span>
        </div>
        <p className="justify-strat mb-4 mt-6 flex items-center space-x-2 font-inter text-base font-medium lg:text-wrap xl:text-nowrap">
          <span className="flex items-start space-x-2 text-sm">
            <IconEye className="lg:mt-1 xl:my-auto" />
            <span>
              <span className="pr-1 font-bold text-primary">{viewerCount} People</span> are viewing right now
            </span>
          </span>
        </p>
        <div className="rounded-3xl bg-white">
          <TestDriveButton car={car} />
          <div className="flex space-x-4">
            <Button
              buttonText="Call Us"
              bgColor="primary-light"
              textColor="primary"
              className="my-2 w-1/2 py-4 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white"
              onClick={() => {
                if (isTabletOrLarger) {
                  setIsPhoneNumberModalOpen(true);
                } else {
                  window.location.href = `tel:${phoneNumber}`;
                }
              }}
            />
            <Button
              buttonText="Buy this Car"
              bgColor="primary-light"
              disabled={buttonDisabled}
              textColor="primary"
              className="my-2 w-1/2 py-4 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set("deposit", "5000");
                const routeTo = trim
                  ? `/book_car/${refId}-${slugify(make)}-${slugify(model)}-${slugify(trim)}`
                  : `/book_car/${refId}-${slugify(make)}-${slugify(model)}`;
                router.push(`${routeTo}?${params.toString()}`);
              }}
            />
          </div>
        </div>
      </div>
      <WarrantyDownpaymentCard />
      <PhoneNumberModal
        isOpen={isPhoneNumberModalOpen}
        onClose={() => setIsPhoneNumberModalOpen(false)}
        phoneNumber={phoneNumber}
      />
    </div>
  );
};

export default CarSidebar;
